import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

export type AnalysisStatus =
    | "idle"
    | "preparing"
    | "analyzing_presentation"
    | "presentation_results"
    | "generating_speech"
    | "practicing"
    | "analyzing_speech"
    | "completed";

interface AnalysisState {
    status: AnalysisStatus;
    step: number;
    analysisData: any;
    presentationFile: { name: string; size: number; type: string } | null; // We can't store the actual File object in localStorage
    jobId: string | null;
    performanceId: string | null;
    speechAnalysisJobId: string | null;
    lastPath: string | null;

    // Actions
    setStatus: (status: AnalysisStatus) => void;
    setStep: (step: number) => void;
    setAnalysisData: (data: any) => void;
    setPresentationFile: (file: File | null) => void;
    setJobId: (id: string | null) => void;
    setPerformanceId: (id: string | null) => void;
    setSpeechAnalysisJobId: (id: string | null) => void;
    setLastPath: (path: string | null) => void;
    reset: () => void;
    startNewSession: () => void;
}

export const useAnalysisStore = create<AnalysisState>()(
    persist(
        (set) => ({
            status: "idle",
            step: 1,
            analysisData: {
                scenario: "presentation_only",
                analysis_type: "presentation_only",
            },
            presentationFile: null,
            jobId: null,
            performanceId: null,
            speechAnalysisJobId: null,
            lastPath: null,

            setStatus: (status) => set({ status }),
            setStep: (step) => set({ step }),
            setAnalysisData: (data) =>
                set((state) => ({
                    analysisData:
                        typeof data === "function"
                            ? data(state.analysisData)
                            : { ...state.analysisData, ...data },
                })),
            setPresentationFile: (file) =>
                set({
                    presentationFile: file
                        ? { name: file.name, size: file.size, type: file.type }
                        : null,
                }),
            setJobId: (jobId) => set({ jobId }),
            setPerformanceId: (performanceId) => set({ performanceId }),
            setSpeechAnalysisJobId: (speechAnalysisJobId) =>
                set({ speechAnalysisJobId }),
            setLastPath: (lastPath) => set({ lastPath }),

            reset: () =>
                set({
                    status: "idle",
                    step: 1,
                    analysisData: {
                        scenario: "presentation_only",
                        analysis_type: "presentation_only",
                    },
                    presentationFile: null,
                    jobId: null,
                    performanceId: null,
                    speechAnalysisJobId: null,
                    lastPath: null,
                }),

            startNewSession: () =>
                set({
                    status: "preparing",
                    step: 1,
                    analysisData: {
                        scenario: "presentation_only",
                        analysis_type: "presentation_only",
                    },
                    presentationFile: null,
                    jobId: null,
                    performanceId: null,
                    speechAnalysisJobId: null,
                    lastPath: "/preparation",
                }),
        }),
        {
            name: "analysis-session-storage",
            storage: createJSONStorage(() => localStorage),
            partialize: (state) => {
                // Filter out any non-serializable data like File objects
                const { analysisData, ...rest } = state;
                const filteredAnalysisData = { ...analysisData };

                // Remove File objects from analysisData
                Object.keys(filteredAnalysisData).forEach((key) => {
                    if (filteredAnalysisData[key] instanceof File) {
                        delete filteredAnalysisData[key];
                    }
                });

                return { ...rest, analysisData: filteredAnalysisData };
            },
        },
    ),
);
